import { useState, useEffect, useRef, useCallback } from 'react';
import { listAllowedEmails, addAllowedEmail, removeAllowedEmail } from '../services/franchiseeService';
import { Link, Navigate } from 'react-router-dom';
import { FaEnvelope, FaCheckCircle, FaClock, FaCalendarAlt, FaEnvelopeOpenText, FaPlus, FaTrashAlt, FaSearch } from 'react-icons/fa';
import useDemoPermissions from '../hooks/useDemoPermissions';

// Helper function to format Firestore Timestamp
const formatTimestamp = (timestamp) => {
  if (!timestamp || !timestamp._seconds) return 'N/A';
  const date = new Date(timestamp._seconds * 1000);
  const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true };
  return date.toLocaleDateString('en-AU', options);
};

// Helper function to format status
const formatStatus = (status) => {
    if (!status) return 'N/A';
    return status.charAt(0).toUpperCase() + status.slice(1);
};

const PAGE_SIZE = 20;

const AllowedEmailsPage = () => {
  const { isDemo } = useDemoPermissions();

  const [allowedEmails, setAllowedEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Search state
  const [search, setSearch] = useState('');
  const searchDebounceRef = useRef(null);

  // Pagination state — pageTokens[0] is always null (first page)
  const [pageTokens, setPageTokens] = useState([null]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);

  // State for Add Email Modal
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  // State for Delete Confirmation Modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchEmails = useCallback(async (startAfterDocId, searchTerm) => {
    setLoading(true);
    setError(null);
    try {
      const data = await listAllowedEmails({
        pageSize: PAGE_SIZE,
        startAfterDocId: startAfterDocId || null,
        search: searchTerm || ''
      });
      setAllowedEmails(data.emails || []);
      setHasNextPage(!!data.nextPageDocId);
      // Store the next page cursor into pageTokens if not already there
      if (data.nextPageDocId) {
        setPageTokens(prev => {
          const next = [...prev];
          // currentPage + 1 is the index of the next page token
          next[currentPage + 1] = data.nextPageDocId;
          return next;
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to load allowed emails.');
      setAllowedEmails([]);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Initial load and when page/search changes
  useEffect(() => {
    fetchEmails(pageTokens[currentPage], search);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Effect to clear messages after a timeout
  useEffect(() => {
    let timer;
    if (successMessage || actionError) {
      timer = setTimeout(() => {
        setSuccessMessage(null);
        setActionError(null);
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [successMessage, actionError]);

  // --- Search ---
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      // Reset to first page on new search
      setCurrentPage(0);
      setPageTokens([null]);
      setHasNextPage(false);
      fetchEmails(null, val);
    }, 500);
  };

  // --- Pagination ---
  const handlePrevPage = () => {
    if (currentPage === 0) return;
    setCurrentPage(prev => prev - 1);
  };

  const handleNextPage = () => {
    if (!hasNextPage) return;
    setCurrentPage(prev => prev + 1);
  };

  // --- Add Email Handlers ---
  const handleOpenAddModal = () => {
    setNewEmail('');
    setActionError(null);
    setIsAddModalOpen(true);
  };

  const handleAddEmailSubmit = async (e) => {
    e.preventDefault();
    if (!newEmail) {
      setActionError("Email address cannot be empty.");
      return;
    }
    setIsAdding(true);
    setActionError(null);
    setSuccessMessage(null);

    try {
      const response = await addAllowedEmail(newEmail);
      // Reset to first page and refetch so the new entry is visible
      setCurrentPage(0);
      setPageTokens([null]);
      await fetchEmails(null, search);
      setSuccessMessage(response.message || `Email ${newEmail} added successfully.`);
      setIsAddModalOpen(false);
      setNewEmail('');
    } catch (err) {
      setActionError(err.message || 'Failed to add email.');
    } finally {
      setIsAdding(false);
    }
  };

  // --- Delete Email Handlers ---
  const handleDeleteClick = (email) => {
    setEmailToDelete(email);
    setActionError(null);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!emailToDelete) return;

    setIsDeleting(true);
    setActionError(null);
    setSuccessMessage(null);

    try {
      const response = await removeAllowedEmail(emailToDelete);
      setAllowedEmails(prevEmails => prevEmails.filter(e => e.email !== emailToDelete));
      setSuccessMessage(response.message || `Email ${emailToDelete} removed successfully.`);
      setIsDeleteModalOpen(false);
      setEmailToDelete(null);
    } catch (err) {
      setActionError(err.message || 'Failed to remove email.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (isDemo) return <Navigate to="/" replace />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 font-sans">
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <FaEnvelopeOpenText className="mr-3" /> Allowed Franchisee Emails
        </h1>
        <div className="flex items-center space-x-4">
           <button
              onClick={handleOpenAddModal}
              className="bg-green-600 hover:bg-green-500 text-white font-semibold py-2 px-5 rounded-2xl shadow-sm inline-flex items-center transition duration-300"
           >
              <FaPlus className="mr-2" />
              Add New Email
           </button>
           <Link to="/franchisees" className="text-gray-500 hover:text-gray-900 font-medium transition-colors">
             Back to Franchisees
           </Link>
        </div>
      </div>

      {/* Search Input */}
      <div className="mb-4 relative max-w-md">
        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search by email..."
          value={search}
          onChange={handleSearchChange}
          className="block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
        />
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 border border-green-300 rounded">
          {successMessage}
        </div>
      )}
      {/* Action Error Message */}
      {actionError && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded">
          Error: {actionError}
        </div>
      )}

      {loading && <p className="text-center text-gray-500 py-10">Loading emails...</p>}
      {error && !loading && <p className="text-center text-red-500 bg-red-100 p-3 rounded mb-4">Error: {error}</p>}

      {!loading && !error && (
        <>
          <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-3xl overflow-hidden">
            {allowedEmails.length === 0 ? (
              <p className="text-center text-gray-500 p-6">No allowed emails found.</p>
            ) : (
              <table className="min-w-full leading-normal">
                <thead>
                  <tr className="bg-gray-100 text-left text-gray-600 uppercase text-sm">
                    <th className="px-5 py-3 border-b-2 border-gray-200"><FaEnvelope className="inline mr-1" /> Email Address</th>
                    <th className="px-5 py-3 border-b-2 border-gray-200"><FaCheckCircle className="inline mr-1" /> Status</th>
                    <th className="px-5 py-3 border-b-2 border-gray-200"><FaCalendarAlt className="inline mr-1" /> Date Added</th>
                    <th className="px-5 py-3 border-b-2 border-gray-200"><FaClock className="inline mr-1" /> Date Registered</th>
                    <th className="px-5 py-3 border-b-2 border-gray-200 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allowedEmails.map((item) => (
                    <tr key={item.email} className="hover:bg-gray-50">
                      <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                        <p className="text-gray-900 whitespace-no-wrap">{item.email}</p>
                      </td>
                      <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          item.status === 'registered' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {formatStatus(item.status)}
                        </span>
                      </td>
                      <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                        <p className="text-gray-900 whitespace-no-wrap">{formatTimestamp(item.addedAt)}</p>
                      </td>
                      <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                        <p className="text-gray-900 whitespace-no-wrap">{formatTimestamp(item.registeredAt)}</p>
                      </td>
                      <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm text-center">
                        <button
                          onClick={() => handleDeleteClick(item.email)}
                          className="text-red-500 hover:text-red-700 transition duration-300 disabled:opacity-50"
                          aria-label={`Delete email ${item.email}`}
                          disabled={isDeleting}
                        >
                          <FaTrashAlt />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination Controls */}
          <div className="flex items-center justify-between mt-4">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 0}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition duration-150 text-sm"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">Page {currentPage + 1}</span>
            <button
              onClick={handleNextPage}
              disabled={!hasNextPage}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition duration-150 text-sm"
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* Add Email Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <div className="relative mx-auto p-8 w-full max-w-md shadow-xl rounded-3xl bg-white border border-gray-100">
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Add New Allowed Email</h3>
            <form onSubmit={handleAddEmailSubmit}>
              <div className="mb-4">
                <label htmlFor="new-email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <input
                  type="email"
                  id="new-email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="mt-1 block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                  required
                  placeholder="franchisee@example.com"
                />
              </div>
               {actionError && !successMessage && (
                 <p className="text-sm text-red-600 mb-3">{actionError}</p>
               )}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="bg-white border border-gray-300 text-gray-700 font-semibold py-2 px-5 rounded-full shadow-sm hover:bg-gray-50 transition duration-300"
                  disabled={isAdding}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-600-dark transition duration-150 disabled:opacity-50"
                  disabled={isAdding}
                >
                  {isAdding ? 'Adding...' : 'Add Email'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <div className="relative mx-auto p-8 w-full max-w-md shadow-xl rounded-3xl bg-white border border-gray-100">
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-2">Confirm Deletion</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to remove the email <span className="font-semibold">{emailToDelete}</span> from the allowed list?
            </p>
             {actionError && !successMessage && (
               <p className="text-sm text-red-600 mb-3">{actionError}</p>
             )}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className="bg-white border border-gray-300 text-gray-700 font-semibold py-2 px-5 rounded-full shadow-sm hover:bg-gray-50 transition duration-300"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="bg-red-600 hover:bg-red-500 text-white font-semibold py-2 px-5 rounded-full shadow-sm transition duration-300 disabled:opacity-50"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AllowedEmailsPage;
